import { bianka } from '@fiverr-private/obs';
import { Click } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/click';
import { Impression } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/impression';
import { getContext } from '@fiverr-private/fiverr_context';
import { ClickType, ElementType, Impression_Component } from '@fiverr-private/bianka_js';
import { ContentReport } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/content_report';
import {
    BaseReportParams,
    ErrorReportParams,
    LinkReportParams,
    SubmitReportParams,
    SuccessReportParams,
} from './types';
import { formatReportedComponents, getMappedModerationItemType, getMappedPageName } from './utils';

export const BASE_IMPRESSION_COMPONENT_PAYLOAD: Omit<Impression_Component, 'name' | 'impId'> = {
    level: 1,
    isLeaf: false,
    positionInParent: 1,
    sizeInParent: 1,
    visibleThroughScroll: true,
};

const DOMAIN_NAME = 'cj_moderation';
const SUB_GROUP = 'moderation_event';

const commonMessageFields = {
    domain: { name: DOMAIN_NAME },
    subGroup: SUB_GROUP,
};

export const reportClickedOnItemReport = (params: BaseReportParams) => {
    const { itemId, entityType, sellerId, buyerId } = params;
    const itemType = getMappedModerationItemType(entityType);
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_on_item_report',
            clickType: ClickType.LEFT,
            element: {
                name: 'item_report',
                type: ElementType.BUTTON,
            },
            user: { id: Number(getContext().userId) },
            seller: sellerId ? { id: Number(sellerId) } : { id: Number(getContext().userId) },
            buyer: buyerId ? { id: Number(buyerId) } : { id: Number(getContext().userId) },
            page: {
                name: getMappedPageName(entityType),
                ctxId: getContext().pageCtxId,
            },
        },
        additionalData: {
            type: ContentReport,
            message: {
                ...commonMessageFields,
                itemId,
                itemType,
            },
        },
    });
};

export const reportClickedOnSubmitItemReport = (params: SubmitReportParams) => {
    const {
        itemId,
        entityType,
        sellerId,
        buyerId,
        actionId,
        reason,
        subReason,
        comment,
        cjReportedComponent,
        cjReportIsAnonymous,
    } = params;
    const itemType = getMappedModerationItemType(entityType);

    const buyer = (() => {
        if (cjReportIsAnonymous) {
            return undefined;
        } else if (buyerId) {
            return { id: Number(buyerId) };
        } else {
            return { id: Number(getContext().userId) };
        }
    })();

    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_on_submit_item_report',
            clickType: ClickType.LEFT,
            element: {
                name: 'submit_item_report',
                type: ElementType.BUTTON,
            },
            user: cjReportIsAnonymous ? { id: Number(getContext().userId) } : undefined,
            seller: sellerId ? { id: Number(sellerId) } : { id: Number(getContext().userId) },
            buyer,
            page: {
                name: getMappedPageName(entityType),
                ctxId: getContext().pageCtxId,
            },
        },
        additionalData: {
            type: ContentReport,
            message: {
                ...commonMessageFields,
                itemId,
                itemType,
                actionId,
                reason,
                subReason,
                comment,
                cjReportedComponent: formatReportedComponents(cjReportedComponent),
                cjReportIsAnonymous,
            },
        },
    });
};

export const reportClickedOnCustomerSupportLinkItemReport = (params: LinkReportParams) => {
    const { itemId, entityType, sellerId, buyerId, actionId } = params;
    const itemType = getMappedModerationItemType(entityType);
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_on_customer_support_link_item_report',
            clickType: ClickType.LEFT,
            element: {
                name: 'customer_support_link_item_report',
                type: ElementType.LINK,
                text: { value: 'customer support' },
            },
            user: { id: Number(getContext().userId) },
            seller: sellerId ? { id: Number(sellerId) } : { id: Number(getContext().userId) },
            buyer: buyerId ? { id: Number(buyerId) } : { id: Number(getContext().userId) },
            page: {
                name: getMappedPageName(entityType),
                ctxId: getContext().pageCtxId,
            },
        },
        additionalData: {
            type: ContentReport,
            message: {
                ...commonMessageFields,
                itemId,
                itemType,
                actionId,
            },
        },
    });
};

export const reportClickedOnCommunityStandardsLinkItemReport = (params: LinkReportParams) => {
    const { itemId, entityType, sellerId, buyerId, actionId } = params;
    const itemType = getMappedModerationItemType(entityType);
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_on_community_standards_link_item_report',
            clickType: ClickType.LEFT,
            element: {
                name: 'community_standards_link_item_report',
                type: ElementType.LINK,
                text: { value: 'community standards' },
            },
            user: { id: Number(getContext().userId) },
            seller: sellerId ? { id: Number(sellerId) } : { id: Number(getContext().userId) },
            buyer: buyerId ? { id: Number(buyerId) } : { id: Number(getContext().userId) },
            page: {
                name: getMappedPageName(entityType),
                ctxId: getContext().pageCtxId,
            },
        },
        additionalData: {
            type: ContentReport,
            message: {
                ...commonMessageFields,
                itemId,
                itemType,
                actionId,
            },
        },
    });
};

export const reportViewedItemReportSubmittedSuccessfully = (params: SuccessReportParams) => {
    const {
        itemId,
        entityType,
        sellerId,
        buyerId,
        cjReportId,
        reason,
        subReason,
        comment,
        cjReportedComponent,
        cjReportIsAnonymous,
    } = params;
    const itemType = getMappedModerationItemType(entityType);
    const buyer = (() => {
        if (cjReportIsAnonymous) {
            return undefined;
        } else if (buyerId) {
            return { id: Number(buyerId) };
        } else {
            return { id: Number(getContext().userId) };
        }
    })();
    bianka.reportActivity({
        type: Impression,
        event: {
            type: 'viewed_item_report_submitted_successfully',
            component: {
                ...BASE_IMPRESSION_COMPONENT_PAYLOAD,
                impId: Date.now().toString(),
                name: 'item_report_submitted_successfully',
                type: 'modal',
                text: 'report was submitted successfully',
            },
            user: cjReportIsAnonymous ? { id: Number(getContext().userId) } : undefined,
            seller: sellerId ? { id: Number(sellerId) } : { id: Number(getContext().userId) },
            buyer,
            page: {
                name: getMappedPageName(entityType),
                ctxId: getContext().pageCtxId,
            },
        },
        additionalData: {
            type: ContentReport,
            message: {
                ...commonMessageFields,
                itemId,
                itemType,
                cjReportId,
                reason,
                subReason,
                comment,
                cjReportedComponent: formatReportedComponents(cjReportedComponent),
                cjReportIsAnonymous,
            },
        },
    });
};

export const reportViewedItemReportSubmissionError = (params: ErrorReportParams) => {
    const {
        itemId,
        entityType,
        sellerId,
        buyerId,
        reason,
        subReason,
        comment,
        cjReportedComponent,
        cjReportIsAnonymous,
    } = params;
    const itemType = getMappedModerationItemType(entityType);
    const buyer = (() => {
        if (cjReportIsAnonymous) {
            return undefined;
        } else if (buyerId) {
            return { id: Number(buyerId) };
        } else {
            return { id: Number(getContext().userId) };
        }
    })();
    bianka.reportActivity({
        type: Impression,
        event: {
            type: 'viewed_item_report_submission_error',
            component: {
                ...BASE_IMPRESSION_COMPONENT_PAYLOAD,
                impId: Date.now().toString(),
                name: 'item_report_submission_error',
                type: 'modal',
                text: 'There was an error submitting the report',
            },
            user: cjReportIsAnonymous ? { id: Number(getContext().userId) } : undefined,
            seller: sellerId ? { id: Number(sellerId) } : { id: Number(getContext().userId) },
            buyer,
            page: {
                name: getMappedPageName(entityType),
                ctxId: getContext().pageCtxId,
            },
        },
        additionalData: {
            type: ContentReport,
            message: {
                ...commonMessageFields,
                itemId,
                itemType,
                reason,
                subReason,
                comment,
                cjReportedComponent: formatReportedComponents(cjReportedComponent),
                cjReportIsAnonymous,
            },
        },
    });
};
